import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Check, X, Clock, CheckCircle2, XCircle, RotateCcw, Send, Inbox as InboxIcon,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import confetti from 'canvas-confetti';

type Trade = {
  id: string;
  proposer_id: string;
  receiver_id: string;
  status: 'proposed' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
  score: number | null;
  message: string | null;
  counterpart_name: string;
  counterpart_avatar: string | null;
  created_at: number;
  responded_at?: number | null;
  completed_at?: number | null;
};

export function Trades() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { t } = useI18n();
  const [tab, setTab] = useState<'inbox' | 'outbox'>('inbox');

  const { data: inbox } = useQuery({
    queryKey: ['trades', 'inbox'],
    queryFn: () => api.listTrades('inbox', token!),
  });
  const { data: outbox } = useQuery({
    queryKey: ['trades', 'outbox'],
    queryFn: () => api.listTrades('outbox', token!),
  });

  const trades = (tab === 'inbox' ? inbox?.trades : outbox?.trades) ?? [];

  const actionMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'reject' | 'cancel' | 'complete' }) =>
      api.patchTrade(id, action, token!),
    onSuccess: (_, vars) => {
      if (vars.action === 'accept' || vars.action === 'complete') {
        confetti({
          particleCount: 100, spread: 70, origin: { y: 0.4 },
          colors: ['#c6ff5e', '#5ee0ff', '#ffd166'],
        });
      }
      qc.invalidateQueries({ queryKey: ['trades'] });
    },
  });

  const inboxPending = (inbox?.trades ?? []).filter((t: Trade) => t.status === 'proposed').length;

  return (
    <div className="space-y-5">
      <header className="flex items-center gap-3">
        <button
          onClick={() => nav('/app/matches')}
          aria-label={t('common.back')}
          className="grid h-10 w-10 place-items-center rounded-full glass hover:bg-graphite/40 transition"
        >
          <ArrowLeft size={18} className="text-haze" />
        </button>
        <div>
          <p className="text-2xs uppercase tracking-widest text-haze">{t('trades.eyebrow')}</p>
          <h1 className="font-display font-extrabold text-3xl">{t('trades.title')}</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-ink/40 border border-line/60">
        <button
          onClick={() => setTab('inbox')}
          className={`relative py-2.5 rounded-xl text-sm font-semibold transition ${
            tab === 'inbox' ? 'bg-flame text-white shadow-glow-flame' : 'text-haze hover:text-foam'
          }`}
        >
          <InboxIcon size={14} className="inline mr-1.5" />
          {t('trades.tabInbox')}
          {inboxPending > 0 && (
            <span className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-extrabold ${
              tab === 'inbox' ? 'bg-foam text-flame' : 'bg-lime text-ink shadow-[0_0_10px_rgba(198,255,94,0.6)]'
            }`}>
              {inboxPending}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('outbox')}
          className={`py-2.5 rounded-xl text-sm font-semibold transition ${
            tab === 'outbox' ? 'bg-cyan text-ink shadow-glow-cyan' : 'text-haze hover:text-foam'
          }`}
        >
          <Send size={14} className="inline mr-1.5" />
          {t('trades.tabOutbox')}
        </button>
      </div>

      {/* Lista */}
      <div className="space-y-3">
        {trades.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center">
            <p className="text-haze">
              {tab === 'inbox' ? t('trades.emptyInbox') : t('trades.emptyOutbox')}
            </p>
            <p className="mt-1 text-2xs text-haze">
              {tab === 'inbox' ? t('trades.emptyInboxDesc') : t('trades.emptyOutboxDesc')}
            </p>
          </div>
        ) : (
          trades.map((t: Trade) => (
            <TradeCard
              key={t.id}
              trade={t}
              isOutgoing={tab === 'outbox'}
              busy={actionMut.isPending}
              onAccept={() => actionMut.mutate({ id: t.id, action: 'accept' })}
              onReject={() => actionMut.mutate({ id: t.id, action: 'reject' })}
              onCancel={() => actionMut.mutate({ id: t.id, action: 'cancel' })}
              onComplete={() => actionMut.mutate({ id: t.id, action: 'complete' })}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TradeCard({
  trade, isOutgoing, busy, onAccept, onReject, onCancel, onComplete,
}: {
  trade: Trade;
  isOutgoing: boolean;
  busy: boolean;
  onAccept: () => void;
  onReject: () => void;
  onCancel: () => void;
  onComplete: () => void;
}) {
  const { t } = useI18n();
  const statusLabels: Record<Trade['status'], string> = {
    proposed: t('trades.statusPending'),
    accepted: t('trades.statusAccepted'),
    completed: t('trades.statusCompleted'),
    rejected: t('trades.statusRejected'),
    cancelled: t('trades.statusCancelled'),
  };
  const statusMeta = STATUS_META[trade.status];
  const initials = trade.counterpart_name.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase();
  const date = new Date(trade.created_at);
  const dateStr = date.toLocaleDateString('es', { day: 'numeric', month: 'short' });

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl glass-strong border ${statusMeta.border} p-4 space-y-3`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`shrink-0 grid h-11 w-11 place-items-center rounded-full bg-graphite ring-2 ${statusMeta.ring} font-display font-bold text-sm`}>
          {trade.counterpart_avatar ? (
            <img src={trade.counterpart_avatar} alt={trade.counterpart_name} className="h-full w-full rounded-full object-cover" />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 justify-between">
            <div className="font-display font-bold text-base truncate">
              {(isOutgoing ? t('trades.to') : t('trades.from'))} {trade.counterpart_name}
            </div>
            <span className={`chip text-2xs font-bold shrink-0 ${statusMeta.chipClass}`}>
              <statusMeta.Icon size={11} className="inline mr-0.5" />
              {statusLabels[trade.status]}
            </span>
          </div>
          <div className="text-2xs text-haze mt-0.5">
            {dateStr}
            {trade.score && <> · {Math.round(trade.score * 100)}% match</>}
          </div>
        </div>
      </div>

      {/* Mensaje */}
      {trade.message && (
        <div className="rounded-xl border border-line/60 bg-ink/40 p-3 text-sm text-foam/90 italic">
          "{trade.message}"
        </div>
      )}

      {/* Acciones */}
      {trade.status === 'proposed' && (
        <div className="flex gap-2 pt-1">
          {isOutgoing ? (
            <button
              onClick={onCancel}
              disabled={busy}
              className="btn-ghost flex-1 text-sm"
            >
              <X size={14} /> Cancelar propuesta
            </button>
          ) : (
            <>
              <button
                onClick={onReject}
                disabled={busy}
                className="btn-ghost flex-1 text-sm"
              >
                <X size={14} /> Rechazar
              </button>
              <button
                onClick={onAccept}
                disabled={busy}
                className="btn-primary flex-1 text-sm"
                style={{ background: 'linear-gradient(180deg, #C6FF5E, #AEE849)', color: '#06112E' }}
              >
                <Check size={14} /> Aceptar
              </button>
            </>
          )}
        </div>
      )}

      {trade.status === 'accepted' && (
        <div className="flex flex-col gap-2 pt-1">
          <div className="rounded-xl bg-cyan/10 border border-cyan/40 p-3 text-sm">
            <div className="font-semibold text-cyan">¡Aceptado!</div>
            <div className="text-2xs text-haze mt-0.5">Coordiná el encuentro y marcá como completado cuando intercambien.</div>
          </div>
          <button
            onClick={onComplete}
            disabled={busy}
            className="btn-primary text-sm"
            style={{ background: 'linear-gradient(180deg, #5ee0ff, #4FBFE5)', color: '#06112E' }}
          >
            <CheckCircle2 size={14} /> Marcar completado
          </button>
        </div>
      )}

      {trade.status === 'completed' && (
        <div className="rounded-xl bg-lime/10 border border-lime/40 p-3 text-sm">
          <div className="font-semibold text-lime flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Intercambio completo
          </div>
        </div>
      )}

      {trade.status === 'rejected' && (
        <div className="rounded-xl bg-flame/10 border border-flame/30 p-3 text-2xs text-haze">
          Esta propuesta fue rechazada.
        </div>
      )}

      {trade.status === 'cancelled' && (
        <div className="rounded-xl bg-haze/5 border border-line/40 p-3 text-2xs text-haze">
          Propuesta cancelada.
        </div>
      )}
    </motion.div>
  );
}

const STATUS_META: Record<Trade['status'], {
  label: string;
  Icon: typeof Clock;
  border: string;
  ring: string;
  chipClass: string;
}> = {
  proposed: {
    label: 'Pendiente',
    Icon: Clock,
    border: 'border-cyan/40',
    ring: 'ring-cyan/60 shadow-glow-cyan',
    chipClass: 'text-cyan border-cyan/40 bg-cyan/10',
  },
  accepted: {
    label: 'Aceptado',
    Icon: Check,
    border: 'border-cyan/60',
    ring: 'ring-cyan/80 shadow-glow-cyan',
    chipClass: 'text-cyan border-cyan/40 bg-cyan/15',
  },
  completed: {
    label: 'Completado',
    Icon: CheckCircle2,
    border: 'border-lime/50',
    ring: 'ring-lime/70 shadow-glow-lime',
    chipClass: 'text-lime border-lime/40 bg-lime/15',
  },
  rejected: {
    label: 'Rechazado',
    Icon: XCircle,
    border: 'border-flame/30',
    ring: 'ring-flame/50',
    chipClass: 'text-[#FF6B7A] border-flame/30 bg-flame/10',
  },
  cancelled: {
    label: 'Cancelado',
    Icon: RotateCcw,
    border: 'border-line/40',
    ring: 'ring-haze/40',
    chipClass: 'text-haze border-line/30 bg-white/5',
  },
};
